import React, { useState, useRef, useEffect } from 'react';

interface SearchBarProps {
  onSearch: (query: string) => void;
  isLoading?: boolean;
  initialValue?: string;
  placeholderMessage?: string;
  autoFocus?: boolean;
  deleteOnSubmit?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  isLoading = false, 
  initialValue = '', 
  placeholderMessage = '',
  autoFocus = false,
  deleteOnSubmit = false
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [isMultiline, setIsMultiline] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const scrollHeight = inputRef.current.scrollHeight;
      const maxHeight = 200; // Maximum height in pixels
      const newHeight = Math.min(scrollHeight, maxHeight);
      inputRef.current.style.height = `${newHeight}px`;
      
      // Update multiline state based on content
      setIsMultiline(scrollHeight > 40); // 40px is roughly the height of a single line
    }
  }, [inputValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      onSearch(inputValue.trim());
      if (deleteOnSubmit) {
        setInputValue(''); // Clear the input after submission
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div 
        ref={containerRef}
        className={`relative w-full transition-all duration-200 ${
          isMultiline ? 'max-w-5xl' : 'max-w-4xl'
        } mx-auto`}
      >
        <div className="flex items-start gap-2">
          <div 
            className={`relative flex-1 transition-all duration-200 ${
              isMultiline ? 'min-h-[20px]' : 'h-12'
            }`}
            style={{
              backgroundColor: 'var(--rhea-bg-tertiary)',
              borderRadius: '12px',
              border: '1px solid var(--rhea-card-border)',
              boxShadow: isFocused ? '0 0 0 2px var(--rhea-accent-primary), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
            }}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={`${placeholderMessage}`}
              className="w-full bg-transparent px-6 py-3 outline-none border-none focus:shadow resize-none"
              style={{
                color: 'var(--rhea-text-primary)',
                minHeight: '48px',
                maxHeight: '200px',
                borderRadius: '12px',
                overflowY: 'auto'
              }}
              rows={1}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 rounded-lg hover:opacity-90 disabled:opacity-50 transition-all duration-200 flex items-center justify-center"
            style={{ 
              backgroundColor: 'var(--rhea-accent-primary)',
              color: 'var(--rhea-text-primary)',
              minWidth: '48px',
              minHeight: '56px'
            }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 20 20">
							  <path d="M17.218,2.268L2.477,8.388C2.13,8.535,2.164,9.05,2.542,9.134L9.33,10.67l1.535,6.787c0.083,0.377,0.602,0.415,0.745,0.065l6.123-14.74C17.866,2.46,17.539,2.134,17.218,2.268 M3.92,8.641l11.772-4.89L9.535,9.909L3.92,8.641z M11.358,16.078l-1.268-5.613l6.157-6.157L11.358,16.078z"></path>
              </svg>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SearchBar;